import { useState } from "react";
import { HeartHandshake, Loader2 } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AssociationPicker({ postal, postalOk, selected, onSelect, verified, onVerified }) {
  const { user } = useAuth();
  const [code, setCode] = useState(verified?.code || user?.association_code || "");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  const check = async () => {
    setError("");
    if (!postalOk) { setError("Renseignez d'abord votre code postal (5 chiffres)."); return; }
    if (!code.trim()) { setError("Saisissez le code de votre association."); return; }
    setChecking(true);
    try {
      const { data } = await api.post("/associations/check", { code, postal });
      onVerified({ ...data, typed_code: code });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Vérification impossible, réessayez.");
      onVerified(null);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div data-testid="association-option" className={`rounded-xl border px-4 py-3 transition-colors ${selected ? "border-amber-deep bg-amber-50" : "border-amber-900/15 bg-white"}`}>
      <label className="flex cursor-pointer items-center gap-3">
        <input type="radio" name="shipping-method" data-testid="association-radio" checked={selected} onChange={onSelect} className="accent-amber-deep" />
        <HeartHandshake size={16} className="text-amber-deep" />
        <span className="flex-1 text-sm font-medium text-bark">Livraison via une association</span>
        {verified && <span className="text-sm font-semibold text-amber-deep">{verified.fee_cents ? formatPrice(verified.fee_cents) : "Offerte"}</span>}
      </label>
      {selected && (
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            <input
              data-testid="association-code-field"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase().replace(/\s/g, "")); onVerified(null); setError(""); }}
              placeholder="Code association (ex. ABEILLES42)"
              className="min-w-0 flex-1 rounded-lg border border-amber-900/20 bg-white px-3 py-2 font-mono text-sm uppercase outline-none focus:border-amber-deep"
            />
            <button type="button" data-testid="association-check-button" onClick={check} disabled={checking} className="flex items-center gap-1 rounded-lg bg-amber-deep px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-honey disabled:opacity-60">
              {checking && <Loader2 size={13} className="animate-spin" />} Valider
            </button>
          </div>
          {error && <p data-testid="association-error" className="text-xs font-medium text-terra">{error}</p>}
          {verified && (
            <div data-testid="association-verified" className="rounded-lg bg-white/80 px-3 py-2 text-xs text-bark-soft">
              <p className="font-semibold text-bark" data-testid="association-verified-name">{verified.name}</p>
              {verified.pickup_address && <p data-testid="association-verified-address">Lieu de retrait : {verified.pickup_address}</p>}
              {verified.next_distribution_label && <p data-testid="association-verified-next">Prochaine distribution : <strong className="text-bark">{verified.next_distribution_label}</strong></p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
